import type { MoodboardItem } from '../db/schema/moodboard'

/**
 * Helpers for custom (moodboard-uploaded) fonts. Shared by the backend
 * (autodesign prompt + Playwright render) and the frontend (canvas picker +
 * registration) so a font's family name is identical everywhere it is used.
 *
 * We do not parse the font binary's name table, so the CSS family name is
 * derived from the filename. That derived string is the single canonical handle
 * for the font across the whole pipeline: the picker, the LLM prompt, the
 * Playwright `@font-face`, the resulting node's `fontFamily`, and the editor's
 * FontFace registration all agree on it, which is what lets the font render.
 */

/** `Roboto-Bold.woff2` → `Roboto-Bold`. Strips the extension and reduces to a
 *  valid CSS identifier (a filename can contain characters CSS can't). */
export function fontFamilyFromFilename(name: string): string {
  return name.replace(/\.[^.]+$/, '').replace(/[^a-zA-Z0-9-]/g, '-')
}

const FONT_EXTENSIONS = /\.(ttf|otf|woff2?|ttc)$/i

/** Detect a font file by mime, falling back to the extension. S3 often reports
 *  fonts as `application/octet-stream`, so the mime alone is not reliable. */
export function isFontFile(mime: string, name: string): boolean {
  if (mime.startsWith('font/') || mime.includes('font')) return true
  return FONT_EXTENSIONS.test(name)
}

export type MoodboardCustomFont = {
  family: string
  url: string
  /** The item's per-font note (e.g. "use for headlines"), if the user added one. */
  annotation?: string
}

/** Extract the selectable custom fonts from a moodboard's items, deduped by
 *  derived family name. */
export function moodboardCustomFonts(
  items: MoodboardItem[] | null | undefined,
): MoodboardCustomFont[] {
  const fonts: MoodboardCustomFont[] = []
  const seen = new Set<string>()
  for (const item of items ?? []) {
    if (item.type !== 'file') continue
    if (!isFontFile(item.file.file.type, item.file.file.name)) continue
    // Prefer the real family name parsed from the binary; fall back to the
    // filename for items uploaded before parsing existed (or when it failed).
    const family = item.fontMeta?.family ?? fontFamilyFromFilename(item.file.file.name)
    if (seen.has(family)) continue
    seen.add(family)
    fonts.push({ family, url: item.file.url, annotation: item.annotation ?? undefined })
  }
  return fonts
}
